import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [kullanici, setKullanici] = useState(null);
  const [modulYetkileri, setModulYetkileri] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [oturumMesaj, setOturumMesaj] = useState('');
  const [oturumUyari, setOturumUyari] = useState(false);
  const tokenYenileRef = useRef(null);
  const uyariRef = useRef(null);
  const inaktiviteRef = useRef(null);
  const INAKTIVITE_SURE = 15 * 60 * 1000;

  const modulYukle = () => {
    axios.get('/api/admin/benim-modullerim')
      .then(r => setModulYetkileri(r.data))
      .catch(() => setModulYetkileri([]));
  };

  const inaktiviteSifirla = () => {
    if (inaktiviteRef.current) clearTimeout(inaktiviteRef.current);
    inaktiviteRef.current = setTimeout(() => {
      cikisYap('15 dakika hareketsizlik nedeniyle oturumunuz sonland\u0131r\u0131ld\u0131.');
    }, INAKTIVITE_SURE);
  };

  const inaktiviteBaslat = () => {
    const olaylar = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    olaylar.forEach(olay => window.addEventListener(olay, inaktiviteSifirla, { passive: true }));
    inaktiviteSifirla();
  };

  const inaktiviteDurdur = () => {
    if (inaktiviteRef.current) clearTimeout(inaktiviteRef.current);
    const olaylar = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    olaylar.forEach(olay => window.removeEventListener(olay, inaktiviteSifirla));
  };

  const tokenYenileZamanla = (tokenGecerlilik) => {
    if (tokenYenileRef.current) clearTimeout(tokenYenileRef.current);
    if (uyariRef.current) clearTimeout(uyariRef.current);
    const gecerlilik = new Date(tokenGecerlilik).getTime();
    const simdi = Date.now();
    const kalanMs = gecerlilik - simdi;
    const uyariMs = kalanMs - 5 * 60 * 1000;
    if (uyariMs > 0) {
      uyariRef.current = setTimeout(() => { setOturumUyari(true); }, uyariMs);
    }
    const yenileMs = kalanMs - 60 * 1000;
    if (yenileMs > 0) {
      tokenYenileRef.current = setTimeout(async () => { await tokenYenile(); }, yenileMs);
    }
  };

  const tokenYenile = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) { cikisYap(); return; }
    try {
      const res = await axios.post('/api/auth/token-yenile', { refreshToken });
      const { token, refreshToken: yeniRefresh, tokenGecerlilik } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', yeniRefresh);
      localStorage.setItem('tokenGecerlilik', tokenGecerlilik);
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      setOturumUyari(false);
      tokenYenileZamanla(tokenGecerlilik);
    } catch(err) {
      cikisYap('Oturum suresi doldu. Lutfen tekrar giris yapin.');
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const k = localStorage.getItem('kullanici');
    const tokenGecerlilik = localStorage.getItem('tokenGecerlilik');
    if (token && k) {
      if (tokenGecerlilik && new Date(tokenGecerlilik) < new Date()) {
        tokenYenile();
      } else {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
        setKullanici(JSON.parse(k));
        modulYukle();
        if (tokenGecerlilik) tokenYenileZamanla(tokenGecerlilik);
        inaktiviteBaslat();
      }
    }
    setYukleniyor(false);

    const interceptor = axios.interceptors.response.use(
      res => res,
      async err => {
        const originalReq = err.config;
        if (err.response?.status === 401 && err.response?.data?.kod === 'TOKEN_EXPIRED' && !originalReq._retry) {
          originalReq._retry = true;
          await tokenYenile();
          const yeniToken = localStorage.getItem('token');
          if (yeniToken) {
            originalReq.headers['Authorization'] = 'Bearer ' + yeniToken;
            return axios(originalReq);
          }
        }
        if (err.response?.status === 401 && localStorage.getItem('token')) {
          cikisYap('Oturum suresi doldu. Lutfen tekrar giris yapin.');
        }
        return Promise.reject(err);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
      if (tokenYenileRef.current) clearTimeout(tokenYenileRef.current);
      if (uyariRef.current) clearTimeout(uyariRef.current);
      inaktiviteDurdur();
    };
  }, []);

  const girisYap = async (email, sifre, captchaToken) => {
    const res = await axios.post('/api/auth/giris', { email, sifre, captchaToken });
    const { token, refreshToken, tokenGecerlilik, kullanici: k } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken || '');
    localStorage.setItem('tokenGecerlilik', tokenGecerlilik || '');
    localStorage.setItem('kullanici', JSON.stringify(k));
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    setKullanici(k);
    setOturumMesaj('');
    setOturumUyari(false);
    if (tokenGecerlilik) tokenYenileZamanla(tokenGecerlilik);
    axios.get('/api/admin/benim-modullerim').then(r => setModulYetkileri(r.data)).catch(() => setModulYetkileri([]));
    inaktiviteBaslat();
    return k;
  };

  const cikisYap = async (mesaj) => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      await axios.post('/api/auth/cikis', { refreshToken });
    } catch(e) {}
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenGecerlilik');
    localStorage.removeItem('kullanici');
    delete axios.defaults.headers.common['Authorization'];
    setKullanici(null);
    setModulYetkileri([]);
    if (mesaj) setOturumMesaj(mesaj);
    if (tokenYenileRef.current) clearTimeout(tokenYenileRef.current);
    if (uyariRef.current) clearTimeout(uyariRef.current);
    inaktiviteDurdur();
    setOturumUyari(false);
  };

  const yetkiVar = (modul) => {
    if (!modulYetkileri || !modulYetkileri.length) return false;
    if (Array.isArray(modulYetkileri) && modulYetkileri[0]?.anahtar) {
      return modulYetkileri.some(m => m.anahtar === modul);
    }
    return modulYetkileri.includes('*') || modulYetkileri.includes(modul);
  };

  return (
    <AuthContext.Provider value={{ kullanici, girisYap, cikisYap, yukleniyor, oturumMesaj, oturumUyari, tokenYenile, modulYetkileri, yetkiVar }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
